/**
 * 產品 create／update 寫入 payload。
 * materials／extraItems：replace-all；編 lineNo；既有列保留 id。
 * 禁止送 status；materials 只送 mtrId／qtyPerProduct／remark（不送 material／unitId／unitName）。
 */
export function buildProductWritePayload(values, { updatedAt = null } = {}) {
  const payload = {
    productNo: String(values?.productNo ?? '').trim(),
    name: String(values?.name ?? '').trim(),
    salesUnitName: String(values?.salesUnitName ?? '').trim(),
    customerId: emptyToNull(values?.customerId),
    customerProductNo: emptyToNull(values?.customerProductNo),
    remark: emptyToNull(values?.remark),
    materials: mapMaterialRowsWithLineNo(values?.materials),
    extraItems: mapExtraItemRowsWithLineNo(values?.extraItems),
  };

  if (updatedAt != null && updatedAt !== '') {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

export function mapProductDetailToForm(detail) {
  return {
    productNo: detail?.productNo ?? '',
    name: detail?.name ?? '',
    salesUnitName: detail?.salesUnitName ?? '',
    customerId: detail?.customerId ?? '',
    customerProductNo: detail?.customerProductNo ?? '',
    remark: detail?.remark ?? '',
    materials: (detail?.materials ?? []).map(mapMaterialToForm),
    extraItems: (detail?.extraItems ?? []).map(mapExtraItemToForm),
  };
}

export function createEmptyMaterialRow(overrides = {}) {
  return {
    mtrId: '',
    mtrNo: '',
    mtrName: '',
    baseUnitName: '',
    qtyPerProduct: 1,
    remark: '',
    ...overrides,
  };
}

export function createEmptyExtraItemRow(overrides = {}) {
  return {
    itemName: '',
    qtyPerProduct: 0,
    unitName: '',
    remark: '',
    ...overrides,
  };
}

/** MtrPickerDialog 列 → 產品物料列。 */
export function mapMtrPickerRowToProductMaterial(row) {
  const mtrId = row?.optionMtrId ?? row?.id ?? '';
  if (!mtrId) {
    return null;
  }

  return createEmptyMaterialRow({
    mtrId,
    mtrNo: row.mtrNo ?? '',
    mtrName: row.name ?? '',
    baseUnitName: resolveBaseUnitName(row.baseUnit),
    qtyPerProduct: 1,
  });
}

function emptyToNull(value) {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isNaN(number) ? fallback : number;
}

function withRowId(mapped, row) {
  if (row?.id) {
    mapped.id = row.id;
  }
  return mapped;
}

function mapMaterialRowsWithLineNo(rows) {
  return (rows ?? []).map((row, index) => withRowId({
    lineNo: (index + 1) * 10,
    mtrId: String(row?.mtrId ?? '').trim(),
    qtyPerProduct: toNumber(row?.qtyPerProduct, 0),
    remark: emptyToNull(row?.remark),
  }, row));
}

function mapExtraItemRowsWithLineNo(rows) {
  return (rows ?? []).map((row, index) => withRowId({
    lineNo: (index + 1) * 10,
    itemName: String(row?.itemName ?? '').trim(),
    qtyPerProduct: toNumber(row?.qtyPerProduct, 0),
    unitName: emptyToNull(row?.unitName),
    remark: emptyToNull(row?.remark),
  }, row));
}

function resolveBaseUnitName(baseUnit) {
  if (!baseUnit) {
    return '';
  }
  if (typeof baseUnit === 'string') {
    return baseUnit;
  }
  return baseUnit.name ?? baseUnit.code ?? baseUnit.unitName ?? '';
}

function mapMaterialToForm(row) {
  const material = row?.material ?? {};
  return {
    id: row?.id,
    mtrId: row?.mtrId ?? material.id ?? '',
    mtrNo: material.mtrNo ?? '',
    mtrName: material.name ?? '',
    baseUnitName: resolveBaseUnitName(material.baseUnit),
    qtyPerProduct: row?.qtyPerProduct ?? 1,
    remark: row?.remark ?? '',
  };
}

function mapExtraItemToForm(row) {
  return {
    id: row?.id,
    itemName: row?.itemName ?? '',
    qtyPerProduct: row?.qtyPerProduct ?? 0,
    unitName: row?.unitName ?? '',
    remark: row?.remark ?? '',
  };
}
